<template>
  <div class="relative" data-test-id="fcc-cases-container">
    <div
      class="px-4 space-y-4 bg-surface-0 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700"
    >
      <CasesFilters class="sticky top-[3.6rem] z-20 py-4 w-full">
        <!-- Custom refresh button based on context -->
        <template #refresh-button>
          <Button
            label="Refresh"
            @click="handleRefresh"
            icon="pi pi-refresh"
            size="small"
            :loading="casesLoading"
            :disabled="casesLoading"
            data-test-id="cases-refresh-button"
          />
        </template>
      </CasesFilters>

      <!-- CasesTable -->
      <CasesTable
        ref="casesTableRef"
        class=""
        :showMerchantColumn="true"
        :casesData="displayCases"
        :pagination="undefined"
        :paginationLoading="false"
        :initialLoading="casesLoading"
      />

      <!-- Empty State -->
      <div
        v-if="!casesLoading && filteredCases.length === 0"
        class="text-center py-12"
      >
        <h3 class="text-lg font-medium text-surface-900 dark:text-surface-0 mb-2">
          No cases found
        </h3>
        <p class="text-surface-500 dark:text-surface-400">
          {{
            hasActiveFilters
              ? 'Try adjusting your filters to see more cases.'
              : 'No cases have been created yet.'
          }}
        </p>
        <Button
          v-if="hasActiveFilters"
          label="Clear Filters"
          variant="outlined"
          @click="clearFilters"
          class="mt-4"
        />
      </div>
    </div>

    <!-- Modal Route View -->
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import { useFCCCaseStore } from '@/stores/fcc'
import { Case } from '@/types/fcc'
import CasesFilters from '@/components/fcc/cases/CasesFilters.vue'
import CasesTable from '@/components/fcc/cases/CasesTable.vue'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

const caseStore = useFCCCaseStore()
const toast = useToast()
const frontegg = useFrontegg()
const { hasPerms } = usePerms()
const { filteredCases, casesLoading, filters } = storeToRefs(caseStore)

const casesTableRef = ref()

const displayCases = computed((): Case[] => {
  return filteredCases.value
})

const hasActiveFilters = computed(() => {
  const f = filters.value
  return (
    f.merchant.selected.length > 0 ||
    f.alertType.selected.length > 0 ||
    f.search.value.length > 0 ||
    f.dateRange.start.length > 0 ||
    f.dateRange.end.length > 0
  )
})

function showApiError(err: unknown, title: string) {
  const apiError = normalizeError(err)
  errorToastWithDetails(toast, title, apiError.toToastMessage(), apiError)
}

onMounted(async () => {
  if (!hasPerms(PERMISSIONS.FCC_UPDATE)) return
  // Always kick off cases fetch if the list is empty, regardless of other loading
  if (caseStore.cases.length === 0) {
    try {
      await caseStore.fetchCases(frontegg.authState, undefined, true)
    } catch (err) {
      showApiError(err, 'Failed to load cases')
    }
  }
})

async function handleRefresh() {
  try {
    await caseStore.fetchCases(frontegg.authState, undefined, true)
  } catch (err) {
    showApiError(err, 'Failed to refresh cases')
  }
}

function clearFilters() {
  caseStore.resetFilters()
}
</script>
